import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import Legal from "@/pages/Legal";
import AppShell from "@/components/layout/AppShell";

import AdminDashboard from "@/pages/admin/Dashboard";
import Orders from "@/pages/admin/Orders";
import OrderDetail from "@/pages/admin/OrderDetail";
import Dispatch from "@/pages/admin/Dispatch";
import Customers from "@/pages/admin/Customers";
import CustomerDetail from "@/pages/admin/CustomerDetail";
import Inventory from "@/pages/admin/Inventory";
import Fleet from "@/pages/admin/Fleet";
import Invoices from "@/pages/admin/Invoices";
import Payments from "@/pages/admin/Payments";
import Reports from "@/pages/admin/Reports";
import Users from "@/pages/admin/Users";
import Profile from "@/pages/Profile";
import ThemeInjector from "@/components/ThemeInjector";
import SiteGuard from "@/components/SiteGuard";
import GoogleTagManager from "@/components/GoogleTagManager";
import Compliance from "@/pages/admin/Compliance";
import Documents from "@/pages/admin/Documents";
import Contracts from "@/pages/admin/Contracts";
import Leads from "@/pages/admin/Leads";
import Tenders from "@/pages/admin/Tenders";
import Procurement from "@/pages/admin/Procurement";
import Quality from "@/pages/admin/Quality";
import Tanks from "@/pages/admin/Tanks";
import Vendors from "@/pages/admin/Vendors";
import ReleaseHistory from "@/pages/admin/ReleaseHistory";
import HR from "@/pages/admin/HR";
import AuditLog from "@/pages/admin/AuditLog";
import LiveTracking from "@/pages/admin/LiveTracking";
import OngoingTrips from "@/pages/admin/OngoingTrips";
import AIAssistant from "@/pages/admin/AIAssistant";
import AccountsBI from "@/pages/admin/AccountsBI";
import Settings from "@/pages/admin/Settings";
import RouteOptimizer from "@/pages/admin/RouteOptimizer";
import Integrations from "@/pages/admin/Integrations";
import Products from "@/pages/admin/Products";
import Finance from "@/pages/admin/Finance";
import ServiceRecords from "@/pages/admin/ServiceRecords";
import BowserFuel from "@/pages/admin/BowserFuel";
import Challans from "@/pages/admin/Challans";
import Expenses from "@/pages/admin/Expenses";
import LoadingInvoices from "@/pages/admin/LoadingInvoices";
import Loadings from "@/pages/admin/Loadings";
import LoadingSources from "@/pages/admin/LoadingSources";
import StockAudit from "@/pages/admin/StockAudit";
import CMSEditor from "@/pages/admin/CMSEditor";
import EnvMigration from "@/pages/admin/EnvMigration";
import Support from "@/pages/Support";
import CustomerWallet from "@/pages/customer/Wallet";

import CustomerDashboard from "@/pages/customer/Dashboard";
import BookOrder from "@/pages/customer/BookOrder";
import TrackOrder from "@/pages/customer/TrackOrder";
import CustomerInvoices from "@/pages/customer/Invoices";
import CustomerChallans from "@/pages/customer/Challans";

import DriverTrips from "@/pages/driver/Trips";
import DriverTripDetail from "@/pages/driver/TripDetail";
import DriverSelfFuel from "@/pages/driver/SelfFuel";
import DriverAttendance from "@/pages/driver/Attendance";
import DriverLoadings, { DriverLoadingDetail } from "@/pages/driver/Loadings";
import { InstallPrompt } from "@/components/InstallPrompt";

function Protected({ roles }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    // Preserve context — if the unauthenticated visit was inside a staff/driver area,
    // bounce them to the private staff URL rather than the public customer login.
    const staffContext = location.pathname.startsWith("/app") || location.pathname.startsWith("/driver");
    return <Navigate to={staffContext ? "/ops-console-fx-9k2m4" : "/login"} replace />;
  }
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return <Outlet />;
}

function RoleRouter() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer") return <Navigate to="/portal" replace />;
  if (user.role === "driver") return <Navigate to="/driver" replace />;
  return <Navigate to="/app/dashboard" replace />;
}

/**
 * LandingRoute — the marketing page at "/".
 *
 * Historic behaviour: if the site was launched from an installed PWA
 * (display-mode: standalone), we bounced straight to /login. Product
 * feedback in Feb 2026: this hid the marketing story from anyone who
 * had the PWA installed on their phone, and confused first-time visitors
 * whose Chrome had auto-installed the app for them. Users are asking
 * "why is the login page opening instead of the front page?".
 *
 * New rule: always render <Landing />. If the visitor is ALREADY signed
 * in, we redirect them to their home shell (customer portal / driver app
 * / admin console) — but a signed-out visitor always sees the pitch.
 */
function LandingRoute() {
  const { user } = useAuth();
  if (user) {
    if (user.role === "customer") return <Navigate to="/portal" replace />;
    if (user.role === "driver") return <Navigate to="/driver" replace />;
    return <Navigate to="/app/dashboard" replace />;
  }
  return <Landing />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ThemeInjector />
        <GoogleTagManager />
        <Toaster position="top-right" richColors closeButton />
        <InstallPrompt />
        <Routes>
          <Route element={<SiteGuard />}>
            <Route path="/" element={<LandingRoute />} />
            <Route path="/login" element={<Login mode="customer" />} />
            {/* Staff/admin login — obscure URL (not linked anywhere in the UI).
                Rename this path here + rebuild if you ever need to rotate the secret URL. */}
            <Route path="/ops-console-fx-9k2m4" element={<Login mode="staff" />} />
            {/* Legacy /staff-login → 404 to discourage discovery */}
            <Route path="/staff-login" element={<Navigate to="/" replace />} />
            <Route path="/register" element={<Register />} />
            <Route path="/privacy" element={<Legal kind="privacy" />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
          </Route>

          <Route path="/app-redirect" element={<RoleRouter />} />

          {/* Admin/staff area */}
          <Route element={<Protected roles={["admin", "sales", "dispatcher", "accounts", "operator", "manager"]} />}>
            <Route path="/app" element={<AppShell variant="admin" />}>
              <Route index element={<Navigate to="/app/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="dispatch" element={<Dispatch />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="fleet" element={<Fleet />} />
              <Route path="service-records" element={<ServiceRecords />} />
              <Route path="bowser-fuel" element={<BowserFuel />} />
              <Route path="loadings" element={<Loadings />} />
              <Route path="loading-sources" element={<LoadingSources />} />
              <Route path="users" element={<Users />} />
              <Route path="profile" element={<Profile />} />
              <Route path="contracts" element={<Contracts />} />
              <Route path="leads" element={<Leads />} />
              <Route path="tenders" element={<Tenders />} />
              <Route path="procurement" element={<Procurement />} />
              <Route path="release-history" element={<ReleaseHistory />} />
              <Route path="quality" element={<Quality />} />
              <Route path="vendors" element={<Vendors />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="tracking" element={<LiveTracking />} />
              <Route path="ongoing-trips" element={<OngoingTrips />} />
              <Route path="ai" element={<AIAssistant />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="migration" element={<EnvMigration />} />
              <Route path="settings" element={<Settings />} />
              <Route path="route-optimizer" element={<RouteOptimizer />} />
              <Route path="support" element={<Support />} />

              {/* Stock / operator-scoped modules — no dispatcher access */}
              <Route element={<Protected roles={["admin", "accounts", "operator", "manager", "sales"]} />}>
                <Route path="inventory" element={<Inventory />} />
                <Route path="tanks" element={<Tanks />} />
                <Route path="compliance" element={<Compliance />} />
                <Route path="documents" element={<Documents />} />
              </Route>

              {/* Finance module — admin, accounts, manager only */}
              <Route element={<Protected roles={["admin", "accounts", "manager"]} />}>
                <Route path="invoices" element={<Invoices />} />
                <Route path="payments" element={<Payments />} />
                <Route path="reports" element={<Reports />} />
                <Route path="finance-bi" element={<AccountsBI />} />
                <Route path="finance" element={<Finance />} />
                <Route path="products" element={<Products />} />
                <Route path="hr" element={<HR />} />
                <Route path="loading-invoices" element={<LoadingInvoices />} />
                <Route path="stock-audit" element={<StockAudit />} />
              </Route>

              {/* Landing CMS — admin only */}
              <Route element={<Protected roles={["admin"]} />}>
                <Route path="cms" element={<CMSEditor />} />
              </Route>

              {/* Delivery challans — dispatch + accounts staff */}
              <Route element={<Protected roles={["admin", "accounts", "manager", "dispatcher"]} />}>
                <Route path="challans" element={<Challans />} />
              </Route>

              {/* Trip Expenses — drivers submit, admin/accounts approve */}
              <Route element={<Protected roles={["admin", "accounts", "manager", "driver", "dispatcher"]} />}>
                <Route path="expenses" element={<Expenses />} />
              </Route>
            </Route>
          </Route>

          {/* Customer area */}
          <Route element={<Protected roles={["customer"]} />}>
            <Route path="/portal" element={<AppShell variant="customer" />}>
              <Route index element={<Navigate to="/portal/dashboard" replace />} />
              <Route path="dashboard" element={<CustomerDashboard />} />
              <Route path="book" element={<BookOrder />} />
              <Route path="track/:id" element={<TrackOrder />} />
              <Route path="invoices" element={<CustomerInvoices />} />
              <Route path="challans" element={<CustomerChallans />} />
              <Route path="wallet" element={<CustomerWallet />} />
              <Route path="profile" element={<Profile />} />
              <Route path="support" element={<Support />} />
            </Route>
          </Route>

          {/* Driver area */}
          <Route element={<Protected roles={["driver"]} />}>
            <Route path="/driver" element={<AppShell variant="driver" />}>
              <Route index element={<DriverTrips />} />
              <Route path="trip/:id" element={<DriverTripDetail />} />
              <Route path="self-fuel" element={<DriverSelfFuel />} />
              <Route path="attendance" element={<DriverAttendance />} />
              <Route path="loadings" element={<DriverLoadings />} />
              <Route path="loadings/:id" element={<DriverLoadingDetail />} />
              <Route path="profile" element={<Profile />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
